import React, { InputHTMLAttributes, forwardRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helpText?: string;
  error?: string;
  fullWidth?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, helpText, error, fullWidth = true, className = '', ...props }, ref) => {
    const { darkMode } = useTheme();

    const inputClasses = `
      px-4 py-2 rounded-lg border
      ${
        darkMode
          ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500'
          : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'
      }
      focus:outline-none focus:ring-2 focus:ring-purple-500
      ${error ? 'border-red-500 focus:ring-red-500' : ''}
      ${fullWidth ? 'w-full' : ''}
      ${className}
    `;

    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label && (
          <label
            className={`block text-sm font-semibold mb-1 ${
              darkMode ? 'text-slate-300' : 'text-slate-700'
            }`}
          >
            {label}
          </label>
        )}
        <input ref={ref} className={inputClasses} {...props} />
        {helpText && !error && (
          <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {helpText}
          </p>
        )}
        {error && <p className="text-xs mt-1 text-red-500">{error}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';

interface InlineInputProps extends InputHTMLAttributes<HTMLInputElement> {
  width?: string;
  textAlign?: 'left' | 'center' | 'right';
}

export const InlineInput = forwardRef<HTMLInputElement, InlineInputProps>(
  ({ width = 'w-28', textAlign = 'right', className = '', ...props }, ref) => {
    const { darkMode } = useTheme();

    return (
      <input
        ref={ref}
        className={`
          ${width} px-2 py-1 rounded border
          ${
            darkMode
              ? 'bg-slate-800 border-slate-600 text-white'
              : 'bg-white border-slate-300 text-slate-800'
          }
          focus:outline-none focus:ring-2 focus:ring-purple-500
          text-${textAlign} font-bold
          ${className}
        `}
        {...props}
      />
    );
  }
);

InlineInput.displayName = 'InlineInput';
